import type {
  Benefits,
  CookieSnapshot,
  Gender,
  InternalCookieOptions,
  StorebrandBenefits,
} from './types';

export const MANAGED_COOKIE_NAMES = {
  benefits: 'benefits',
  gender: 'stbg',
  birthYear: 'stby',
  storebrandBenefits: 'stbb',
  stbId: 'stb_id',
  lastSync: 'stb_last_sync',
} as const satisfies Record<keyof CookieSnapshot, string>;

/** Returns a UTC expiry string `hours` hours from now. */
export const getExpiresInHours = (hours: number): string => {
  const MS_PER_HOUR = 3_600_000;
  return new Date(Date.now() + hours * MS_PER_HOUR).toUTCString();
};

/** Returns a UTC expiry string `months` months from now (approximate, 30-day months). */
export const getExpiresInMonths = (months: number): string => {
  const MS_PER_MONTH = 2_592_000_000;
  return new Date(Date.now() + months * MS_PER_MONTH).toUTCString();
};

/** Returns a UTC expiry string `years` years from now (approximate, 365-day years). */
export const getExpiresInYears = (years: number): string => {
  const MS_PER_YEAR = 31_536_000_000;
  return new Date(Date.now() + years * MS_PER_YEAR).toUTCString();
};

/** Reads a cookie value by name, or `undefined` if not set. */
export const getCookie = (name: string): string | undefined => {
  const prefix = `${name}=`;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trimStart();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return undefined;
};

/** Builds a `; domain=…` fragment, or empty string when domain is omitted. */
const buildDomainPart = (cookieDomain?: string): string =>
  cookieDomain ? `;domain=${cookieDomain}` : '';

/**
 * Sets a cookie with standard path/domain/expires parts.
 * Pass extra suffixes (e.g. `;SameSite=Lax`) via `additionalParts`.
 */
export const setCookie = (
  name: string,
  value: string,
  options: InternalCookieOptions,
  expires: string,
  additionalParts = '',
): void => {
  const domainPart = buildDomainPart(options.cookieDomain);
  document.cookie = `${name}=${value};path=${options.path}${domainPart};expires=${expires}${additionalParts}`;
};

/** Removes a cookie by setting its expiry to the epoch. */
export const removeCookie = (
  name: string,
  options: InternalCookieOptions,
): void => {
  setCookie(name, '', options, 'Thu, 01 Jan 1970 00:00:00 GMT');
};

/** Reads the current managed cookie set as a typed snapshot. */
export const buildCookieSnapshot = (): CookieSnapshot => ({
  benefits: getCookie(MANAGED_COOKIE_NAMES.benefits) as Benefits | undefined,
  gender: getCookie(MANAGED_COOKIE_NAMES.gender) as Gender | undefined,
  birthYear: getCookie(MANAGED_COOKIE_NAMES.birthYear),
  storebrandBenefits: getCookie(MANAGED_COOKIE_NAMES.storebrandBenefits) as
    | StorebrandBenefits
    | undefined,
  stbId: getCookie(MANAGED_COOKIE_NAMES.stbId),
  lastSync: getCookie(MANAGED_COOKIE_NAMES.lastSync),
});
